import React from "react";
import { Info, X } from "lucide-react";

import ModalDialog from "../ModalDialog";
import type { FolderTreeNode } from "../../services/fileApi";
import { useDealFileReview } from "../../hooks/useDealFileReview";
import type { ReviewSummary } from "../../utils/addFilesReview";
import AddFilesDropZone from "./AddFilesDropZone";
import FileReviewRow from "./FileReviewRow";

interface AddFilesDialogProps {
	deal: FolderTreeNode;
	initialFiles: File[];
	onClose: () => void;
	/** Called with exactly the files to send, renamed copies included. */
	onConfirm: (files: File[]) => void;
	/** Offered when the analyst reached this deal by choosing it. */
	onChangeDeal?: (files: File[]) => void;
}

const TITLE_ID = "add-files-dialog-title";

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? "" : "s"}`;

const summaryText = ({ added, replaced, skipped }: ReviewSummary): string =>
	[
		added > 0 ? `${added} new` : "",
		replaced > 0 ? `${replaced} updated` : "",
		skipped > 0 ? `${skipped} skipped` : "",
	]
		.filter(Boolean)
		.join(" · ");

/**
 * Review files before they are added to a deal that already exists (DOP-608):
 * every file says what will happen to it, and nothing already in the deal is
 * replaced without the analyst choosing that.
 */
const AddFilesDialog: React.FC<AddFilesDialogProps> = ({
	deal,
	initialFiles,
	onClose,
	onConfirm,
	onChangeDeal,
}) => {
	const review = useDealFileReview(deal, initialFiles);
	const { items, summary, isChecking } = review;
	const hasFiles = items.length > 0;

	const confirmLabel = isChecking
		? "Checking files…"
		: summary.toSend === 0
			? "Nothing new to add"
			: `Add ${plural(summary.toSend, "file")}`;

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onClose} size="lg">
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-xl font-semibold text-text-base">
						Add files to {deal.folder.name}
					</h2>
					<p className="mt-1 text-sm text-text-muted">
						{review.existingCount === 0
							? "This deal has no files yet."
							: `The deal has ${plural(review.existingCount, "file")} now.`}
						{hasFiles ? " Here's what will change." : ""}
						{onChangeDeal && (
							<>
								{" "}
								<button
									type="button"
									onClick={() => onChangeDeal(review.files)}
									className="font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
								>
									Pick a different deal
								</button>
							</>
						)}
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="rounded-md p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					<X className="h-[18px] w-[18px]" aria-hidden="true" />
				</button>
			</div>

			<div className="mt-4">
				<AddFilesDropZone onFiles={review.addFiles} prominent={!hasFiles} />
			</div>

			{hasFiles && (
				<>
					<ul className="mt-1 max-h-[45vh] overflow-y-auto">
						{items.map((item) => (
							<FileReviewRow
								key={item.path}
								item={item}
								choice={review.choices[item.path] ?? "replace"}
								onChoose={review.setChoice}
								onRemove={review.removeFile}
							/>
						))}
					</ul>
					<div className="mt-3 flex gap-2.5 rounded-lg bg-gray-50 px-3.5 py-3 text-sm leading-relaxed text-text-muted">
						<Info
							className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
							aria-hidden="true"
						/>
						<span>
							Your chats and earlier files stay as they are. Doppler will read
							the new files so you can ask about them in chat.
						</span>
					</div>
				</>
			)}

			<div className="mt-5 flex items-center gap-2.5">
				<span className="text-sm text-text-muted">{summaryText(summary)}</span>
				<button
					type="button"
					onClick={onClose}
					className="ml-auto rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					Cancel
				</button>
				<button
					type="button"
					disabled={isChecking || summary.toSend === 0}
					onClick={() => onConfirm(review.buildPlan())}
					className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-[#003a3a] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{confirmLabel}
				</button>
			</div>
		</ModalDialog>
	);
};

export default AddFilesDialog;
